import { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  MenuItem,
  IconButton,
  Pagination,
  Paper,
  InputLabel,
  Skeleton,
  Grid,
  Checkbox,
  ListItemText
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import adminLoginBg from "../../assets/images/background.png";
import { useApi } from "../../api/useApi";
import Notification from "../Notification";
import BackButton from "../BackButton";

export const validateQuestion = (q) => {
  if (!(q.questions?.trim() || q.questionText?.trim())) return "Question text is required.";
  if (!q.userRole?.trim()) return "User role is required.";
  if (!q.category?.trim()) return "Category is required.";
  if (!q.options?.length) return "At least one option is required.";

  for (const [idx, o] of q.options.entries()) {
    if (!o.optionText?.trim()) return `Option ${idx + 1}: Option text is required.`;
    if (q.isWeightage && (o.optionPoints === "" || o.optionPoints === null))
      return `Option ${idx + 1}: Weight percentage is required.`;
    if (!o.recommendationText?.trim()) return `Option ${idx + 1}: Recommendation is required.`;
    if (!(o.remarkText?.trim() || o.remarks?.trim())) return `Option ${idx + 1}: Remarks are required.`;
  }

  return null;
};

function OverflowTooltipTextField({ value, onChange, ...props }) {
  const inputRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (inputRef.current) {
      setIsOverflowing(inputRef.current.scrollWidth > inputRef.current.clientWidth);
    }
  }, [value]);

  return (
    <TextField
      {...props}
      value={value}
      onChange={onChange}
      inputRef={inputRef}
      InputProps={{
        ...props.InputProps,
        sx: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          ...(props.InputProps?.sx || {}),
        },
      }}
    />
  );
}

export default function AddNewQuestionaire() {
  const location = useLocation();
  const navigate = useNavigate();
  const domain = location.pathname.split("/")[2];
  const incomingQuestions = location.state?.questions || [];

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [userDomain, setUserDomain] = useState([]);
  const [notif, setNotif] = useState({ open: false, success: true, text: "" });
  const { request, loading } = useApi();
  const [loadingData, setLoadingData] = useState(true);

  // Fetch roles and categories
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const allQuestions = await request({ url: "/questions/all" });
        const rolesSet = new Set();
        const categoriesSet = new Set();
        const userDomainSet = new Set();
        for (const q of allQuestions) {
          rolesSet.add(q.userRole);
          categoriesSet.add(q.category);
          userDomainSet.add(q.userDomainRole);
        }
        const uniqueCategories = [
          ...new Set(
            [...userDomainSet]
              .filter(Boolean)
              .flatMap((item) => item.split("/"))
              .map((item) => item.trim())
          ),
        ];
        console.log('uniqueCategories', uniqueCategories)
        setRoles([...rolesSet]);
        setCategories([...categoriesSet]);
        setUserDomain([...uniqueCategories]);
      } catch (err) {
        console.error("Failed to load questions", err);
      } finally {
        setLoadingData(false);
      }
    };
    fetchQuestions();
  }, []);

  // Initialize data
  useEffect(() => {
    if (incomingQuestions.length > 0) {
      const formatted = incomingQuestions.map((q) => {
        const optionTexts = q.optionText?.split(",") || [];
        const optionPoints = q.optionPoints?.split(",") || [];
        const recommendations = q.recommendation?.split("||") || [];
        const remarks = q.remarks?.split(",") || [];
        const options = optionTexts.map((text, idx) => ({
          optionText: text,
          optionPoints: parseFloat(optionPoints[idx] || 0),
          recommendationText: recommendations[idx] || "",
          remarkText: remarks[idx] || "",
        }));
        return {
          ...q,
          options,
          questionIsMandatory: q.questionIsMandatory || false,
          isWeightage: Boolean(optionPoints.length),
          userRole: q.userRole || "",
          category: q.category || "",
          userDomainRole: q.userDomainRole || ""
        };
      });
      setData(formatted);
    } else {
      setData([
        {
          questions: "",
          options: [{ optionText: "", optionPoints: 0, recommendationText: "", remarkText: "" }],
          questionIsMandatory: false,
          isWeightage: false,
          userRole: "",
          category: "",
          type: domain.toUpperCase(),
        },
      ]);
    }
  }, [location.state?.questions, domain]);

  const currentQuestion = data[currentPage - 1];

  const isLoading = loading || data.length === 0 || !currentQuestion;

  const handleOptionChange = (index, field, value) => {
    const updated = [...data];
    updated[currentPage - 1].options[index][field] = value;
    setData(updated);
  };

  const addOption = () => {
    setData((prevData) => {
      const updated = [...prevData];
      const currentQ = { ...updated[currentPage - 1] }; // copy current question
      currentQ.options = [
        ...currentQ.options,
        { optionText: "", optionPoints: 0, recommendationText: "", remarkText: "" },
      ]; // copy options + add new
      updated[currentPage - 1] = currentQ; // update question in array
      return updated;
    });
  };

  const deleteOption = (index) => {
    const updated = [...data];
    if (updated[currentPage - 1].options.length === 1) {
      setNotif({ open: true, success: false, text: "At least one option is required." });
      return;
    }
    updated[currentPage - 1].options.splice(index, 1);
    setData(updated);
  };

  const handleSaveCurrent = async () => {
    const q = data[currentPage - 1];
    console.log('current question to save', q);
    const error = validateQuestion(q);
    if (error) {
      setNotif({ open: true, success: false, text: error });
      return;
    }
    try {
      const payload = {
        questionText: q.questions,
        userRole: q.userRole,
        domain: domain.toUpperCase(),
        //userDomainRoles: q.userDomainRole,
        userDomainRoles: q.userDomainRole
  ? q.userDomainRole.split("/").map(role => role.trim())
  : [],
        category: q.category,
        questionIsMandatory: q.questionIsMandatory,
        options: q.options.map((o) => ({
          optionText: o.optionText,
          optionPoints: o.optionPoints,
          recommendationText: o.recommendationText || "",
          remarks: o.remarkText || "",
        })),
      };
      await request({ url: `/questions`, method: "POST", data: payload });
      setNotif({ open: true, success: true, text: "Current question saved successfully." });
      setTimeout(() => navigate(`/admin/${domain}/questionaire`), 1000);
    } catch (err) {
      console.error(err);
      setNotif({ open: true, success: false, text: "Failed to save current question." });
    }
  };

  const handleSaveAll = async () => {
    for (let i = 0; i < data.length; i++) {
      const error = validateQuestion(data[i]);
      if (error) {
        setNotif({ open: true, success: false, text: `Question ${i + 1}: ${error}` });
        return;
      }
    }
    try {
     const payload = data.map((q) => ({
  questionText: q.questions,
  userRole: q.userRole,
  domain: domain.toUpperCase(),
  userDomainRoles: q.userDomainRole
    ? q.userDomainRole.split("/").map(role => role.trim())
    : [],
  category: q.category,
  questionIsMandatory: q.questionIsMandatory,
  options: q.options.map((o) => ({
    optionText: o.optionText,
    optionPoints: o.optionPoints,
    recommendationText: o.recommendationText || "",
    remarks: o.remarkText || "",
  })),
}));
      await request({ url: "/questions/upload-excel-json", method: "POST", data: payload });
      setNotif({ open: true, success: true, text: "All questions saved successfully." });
    } catch (err) {
      console.error(err);
      setNotif({ open: true, success: false, text: "Failed to save all questions." });
    }
  };

  return (
    <>
      <Grid container component="main" sx={{ minHeight: "90vh", position: "relative", display: "flex", justifyContent: "center", alignItems: "center", p: 2 }}>
        {/* Background */}
        <Box sx={{
          position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
          // backgroundImage: `url(${adminLoginBg})`,
          // backgroundSize: "cover",
          opacity: "0.2",
          zIndex: -1
        }} />
        <Box sx={{ 
          position: "absolute", top: 0, left: 0, width: "100%", height: "100%", 
          // background: "conic-gradient(from -7.72deg at 100% 100%, #000000 0deg, #49454F 294.45deg, #000000 360deg)", 
          // opacity: 1, 
          // backdropFilter: "blur(20px) saturate(180%)", 
          // WebkitBackdropFilter: "blur(12px) saturate(180%)", 
          zIndex: -2 }} />

        {/* Main Paper */}
        <Paper sx={{ display: "flex", p: 4, borderRadius: 4, maxWidth: "100%", width: "77vw", overflowY: "auto", boxShadow: 6, background: "rgba(255,255,255,0.15)", backdropFilter: "blur(5px) saturate(180%)", flexDirection: "column" }}>
          <BackButton/>
          <Typography variant="h5" sx={{ ml: 2, mb: 2 }}>
            Add Questionaire: <span style={{ fontWeight: "bold" }}>{domain.toLocaleUpperCase()}</span>
          </Typography>

          <Box sx={{ mb: 1, p: 2, borderRadius: 5, backgroundColor: "rgba(255,255,255,0.7)" }}>
            {/* Switches */}
            {loadingData || !currentQuestion ? (
              <Box display="flex" gap={4} mt={2}>
                <Skeleton variant="rectangular" width={200} height={40} />
                <Skeleton variant="rectangular" width={200} height={40} />
              </Box>
            ) : (
              <Box display="flex" gap={4} mt={2}>
                <FormControlLabel
                  control={<Switch checked={currentQuestion.questionIsMandatory} onChange={(e) => { const updated = [...data]; updated[currentPage - 1].questionIsMandatory = e.target.checked; setData(updated); }} />}
                  label="Is this question mandatory?"
                />
                <FormControlLabel
                  control={<Switch checked={currentQuestion.isWeightage} onChange={(e) => { const updated = [...data]; updated[currentPage - 1].isWeightage = e.target.checked; setData(updated); }} />}
                  label="Is there a weightage for this question?"
                />
              </Box>
            )}

            {/* Role + Category + Domain */}
            <Box display="flex" gap={2} mt={2}>
              {[roles, categories, userDomain].map((list, idx) => (
                loadingData ? (
                  <Skeleton key={idx} variant="rectangular" width="100%" height={56} />
                ) : null
              ))}
              {!loadingData && currentQuestion && (
                <>
                  {/* Role Dropdown */}
                  <Box display="flex" flexDirection="column" flex={1}>
                    <InputLabel sx={{ mb: 1, ml: 1, color: "black" }} required>Select Role</InputLabel>
                    <TextField select fullWidth value={currentQuestion.userRole} onChange={(e) => { const updated = [...data]; updated[currentPage - 1].userRole = e.target.value; setData(updated); }} variant="outlined" sx={{ borderRadius: "24px", backgroundColor: "whitesmoke", "& .MuiOutlinedInput-notchedOutline": { borderRadius: "24px" } }}>
                      {roles?.map((role) => <MenuItem key={role} value={role}>{role}</MenuItem>)}
                    </TextField>
                  </Box>

                  {/* Category Dropdown */}
                  <Box display="flex" flexDirection="column" flex={1}>
                    <InputLabel sx={{ mb: 1, ml: 1, color: "black" }} required>Category</InputLabel>
                    <TextField select fullWidth value={currentQuestion.category} onChange={(e) => { const updated = [...data]; updated[currentPage - 1].category = e.target.value; setData(updated); }} variant="outlined" sx={{ borderRadius: "24px", backgroundColor: "whitesmoke", "& .MuiOutlinedInput-notchedOutline": { borderRadius: "24px" } }}>
                      {categories?.map((cat) => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
                    </TextField>
                  </Box>

                  {/* DomainRole Dropdown */}
                  <Box display="flex" flexDirection="column" flex={1}>
                    <InputLabel sx={{ mb: 1, ml: 1, color: "black" }} required>
                      Domain Role
                    </InputLabel>
                    {isLoading ? (
                      <Skeleton variant="rounded" height={56} />
                    ) : (
                      <TextField
                        select
                        fullWidth
                        SelectProps={{
                          multiple: true,
                          renderValue: (selected) => selected.join(", "),
                        }}
                        sx={{
                          borderRadius: "24px",
                          backgroundColor: "whitesmoke",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderRadius: "24px",
                          },
                        }}
                        value={
                          currentQuestion?.userDomainRole
                            ? currentQuestion.userDomainRole.split("/")
                            : []
                        }
                        onChange={(e) => {
                          const updated = [...data];

                          updated[currentPage - 1].userDomainRole =
                            e.target.value.join("/");

                          setData(updated);
                        }}
                      >
                        {userDomain?.map((cat) => (
                          <MenuItem key={cat} value={cat}>
                            <Checkbox
                              checked={
                                currentQuestion?.userDomainRole
                                  ?.split("/")
                                  ?.includes(cat)
                              }
                            />
                            <ListItemText primary={cat} />
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Box>

                  {/* Domain */}
                  <Box display="flex" flexDirection="column" flex={1}>
                    <InputLabel sx={{ mb: 1, ml: 1, color: "black" }}>Domain</InputLabel>
                    <TextField fullWidth disabled value={domain.toUpperCase()} variant="outlined" sx={{ borderRadius: "24px", backgroundColor: "whitesmoke", "& .MuiOutlinedInput-notchedOutline": { borderRadius: "24px" } }} />
                  </Box>
                </>
              )}
            </Box>

            {/* Question Text */}
            <Box display="flex" flexDirection="column" mt={2}>
              <InputLabel sx={{ mb: 1, ml: 1, color: "black" }}>Enter your question</InputLabel>
              {loadingData || !currentQuestion ? (
                <Skeleton variant="rectangular" width="100%" height={80} />
              ) : (
                <TextField multiline rows={4} fullWidth required value={currentQuestion.questions} onChange={(e) => { const updated = [...data]; updated[currentPage - 1].questions = e.target.value; setData(updated); }} variant="outlined" sx={{ borderRadius: "16px", backgroundColor: "whitesmoke", "& .MuiOutlinedInput-notchedOutline": { borderRadius: "16px" } }} />
              )}
            </Box>

            {/* Options */}
            <Box mt={3}>
              <Typography variant="subtitle1">Options</Typography>
              {loadingData || !currentQuestion ? (
                [0, 1].map((_, idx) => (
                  <Skeleton key={idx} variant="rectangular" width="100%" height={56} sx={{ mt: 2 }} />
                ))
              ) : (
                currentQuestion.options?.map((opt, index) => (
                  <Box key={index} display="flex" gap={2} alignItems="flex-start" mt={2}>
                    <OverflowTooltipTextField
                      select
                      label="Enter Option"
                      value={opt.optionText}
                      onChange={(e) =>
                        handleOptionChange(
                          index,
                          "optionText",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      fullWidth
                      sx={{
                        backgroundColor: "whitesmoke",
                        borderRadius: "24px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderRadius: "24px",
                        },
                      }}
                    >
                      {["Yes", "No", "Partially"].map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </OverflowTooltipTextField>

                    <OverflowTooltipTextField
                        select
                        label="Weight Percentage"
                        value={opt.optionPoints}
                        onChange={(e) =>
                          handleOptionChange(
                            index,
                            "optionPoints",
                            e.target.value
                          )
                        }
                        variant="outlined"
                        fullWidth
                        sx={{
                          backgroundColor: "whitesmoke",
                          borderRadius: "24px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderRadius: "24px",
                          },
                        }}
                      >
                        {[1, 0.5, 0].map((weight) => (
                          <MenuItem
                            key={weight}
                            value={weight}
                          >
                            {weight}
                          </MenuItem>
                        ))}
                      </OverflowTooltipTextField>


                    <OverflowTooltipTextField label="Recommendation" value={opt.recommendationText} onChange={(e) => handleOptionChange(index, "recommendationText", e.target.value)} variant="outlined" fullWidth sx={{ backgroundColor: "whitesmoke", borderRadius: "24px", "& .MuiOutlinedInput-notchedOutline": { borderRadius: "24px" } }} />
                    <OverflowTooltipTextField
                      select
                      label="Remark"
                      value={opt.remarkText}
                      onChange={(e) =>
                        handleOptionChange(
                          index,
                          "remarkText",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      fullWidth
                      sx={{
                        backgroundColor: "whitesmoke",
                        borderRadius: "24px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderRadius: "24px",
                        },
                      }}
                    >
                      {[
                        "Excellent",
                        "Good",
                        "Needs Improvement"
                      ].map((remark) => (
                        <MenuItem
                          key={remark}
                          value={remark}
                        >
                          {remark}
                        </MenuItem>
                      ))}
                    </OverflowTooltipTextField>
                    <IconButton onClick={() => deleteOption(index)} color="error">
                      <Delete />
                    </IconButton>
                  </Box>
                ))
              )}
              {!loadingData && currentQuestion && (
                <Button startIcon={<Add />} onClick={addOption} sx={{ mt: 2 }}>Add Option</Button>
              )}
            </Box>

            {/* Actions */}
            {!loadingData && currentQuestion && (
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
                <Box display="flex" justifyContent="flex-start" alignItems="center" gap={2}>
                  <Button sx={{ backgroundColor: "#ffffff", color: "#0036DD", px: 6, py: 1.5, borderRadius: "30px" }} variant="contained" onClick={() => navigate(`/admin/${domain}/questionaire`)}>Cancel</Button>
                  {data.length > 1 && currentPage < data.length && (
                    <Button onClick={() => setCurrentPage((prev) => Math.min(prev + 1, data.length))} sx={{ backgroundColor: "#ffffff", color: "#0036DD", px: 6, py: 1.5, borderRadius: "30px" }} variant="contained">Next</Button>
                  )}
                </Box>

                <Box display="flex" gap={2}>
                  {data.length > 1 ? (
                    <>
                      <Button sx={{ backgroundColor: "#0036DD", color: "#ffffff", px: 6, py: 1.5, borderRadius: "30px" }} variant="contained" onClick={handleSaveCurrent}>
                        {loading ? <Skeleton variant="rectangular" width={80} height={24} /> : "Save Current"}
                      </Button>
                      <Button sx={{ backgroundColor: "#2E7D32", color: "#ffffff", px: 6, py: 1.5, borderRadius: "30px" }} variant="contained" onClick={handleSaveAll}>
                        {loading ? <Skeleton variant="rectangular" width={80} height={24} /> : "Save All"}
                      </Button>
                    </>
                  ) : (
                    <Button sx={{ backgroundColor: "#0036DD", color: "#ffffff", px: 6, py: 1.5, borderRadius: "30px" }} variant="contained" onClick={handleSaveCurrent}>
                      {loading ? <Skeleton variant="rectangular" width={80} height={24} /> : "Save"}
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {/* Pagination */}
            {!loadingData && currentQuestion && data.length > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination count={data.length} page={currentPage} onChange={(e, page) => setCurrentPage(page)} />
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>

      <Notification open={notif.open} success={notif.success} text={notif.text} onClose={() => setNotif((prev) => ({ ...prev, open: false }))} />
    </>
  );
}